<template>
  <div>
    <h4>Anlagen</h4>
    <u-error-handling
      v-if="errorObject"
      :error-object="errorObject"
      :property-mapping="errorPropertyMappings"
    />
    <u-loader v-if="isLoading" />

    <table
      v-else
      class="w-100 attatchmentsTable ml-2 mt-4"
    >
      <thead>
        <tr>
          <th>Priorität</th>
          <th>Sprache</th>
          <th>Beschreibung</th>
          <th>Datei</th>
          <th>Aktionen</th>
        </tr>
      </thead>

      <tbody>
        <template v-for="(attatchment, index) in allAttatchments">
          <!-- First Row -->
          <tr
            :key="index"
            style="border-bottom: 1px solid #ddd"
          >
            <td
              v-if="currentEditRow == index && isUpdating"
              colspan="5"
              style="text-align: center"
            >
              <u-loader />
            </td>
            <td v-if="currentEditRow != index">
              {{ attatchment.weight }}
            </td>
            <td
              v-else
              v-show="!isUpdating"
            >
              <v-text-field
                v-model="attatchment.weight"
                type="number"
                class="mr-4 normalFontSize"
              />
              <u-validation-checker
                v-model="requiredPropertiesEdit"
                :is-empty="!attatchment.weight.toString()"
                :section="validationSection"
                property-name="weight"
              />
            </td>
            <td v-if="currentEditRow != index">
              <div
                v-for="(language, languageIndex) in allLanguages"
                :key="languageIndex"
              >
                <div
                  v-if="
                    attatchment.contents.filter((content) => {
                      return content.language == language.value;
                    }).length != 0
                  "
                >
                  {{ language.name }}
                </div>
              </div>
            </td>
            <td
              v-else
              v-show="!isUpdating"
            >
              <label>
                <input
                  type="checkbox"
                  name="check"
                  :checked="currentEditRowLanguages.indexOf('de-CH') != -1"
                  @change="languageCheckBoxChanged('de-CH', true)"
                >
                <span class="label-text">Deutsch</span>
              </label>
              <br>
              <label>
                <input
                  type="checkbox"
                  name="check"
                  :checked="currentEditRowLanguages.indexOf('fr-CH') != -1"
                  @change="languageCheckBoxChanged('fr-CH', true)"
                >
                <span class="label-text">Französisch</span>
              </label>
              <br>
              <label>
                <input
                  type="checkbox"
                  name="check"
                  :checked="currentEditRowLanguages.indexOf('it-CH') != -1"
                  @change="languageCheckBoxChanged('it-CH', true)"
                >
                <span class="label-text">Italienisch</span>
              </label>

              <u-validation-checker
                v-model="requiredPropertiesEdit"
                :is-empty="currentEditRowLanguages.length == 0"
                :section="validationSection"
                property-name="content.language"
              />
            </td>
            <td v-if="currentEditRow != index">
              {{ attatchment.contents[0] ? attatchment.contents[0].value : "" }}
            </td>
            <td
              v-else
              v-show="!isUpdating"
            >
              <div
                v-for="(language, languageIndex) in allLanguages"
                :key="languageIndex"
              >
                <v-text-field
                  v-if="
                    attatchment.contents.filter((content) => {
                      return content.language == language.value;
                    }).length != 0
                  "
                  v-model="
                    attatchment.contents.filter((content) => {
                      return content.language == language.value;
                    })[0].value
                  "
                  type="text"
                  multi-line
                  rows="1"
                  auto-grow
                  :placeholder="'Beschreibung ' + language.name"
                  :label="language.name"
                  class="mr-4 normalFontSize"
                />
              </div>
              <u-validation-checker
                v-model="requiredPropertiesEdit"
                :is-empty="
                  attatchment.contents.filter((content) => {
                    return content.value == '';
                  }).length != 0
                "
                :section="validationSection"
                property-name="content.value"
              />
            </td>
            <td v-if="currentEditRow != index">
              <div
                v-for="(language, languageIndex) in allLanguages"
                :key="languageIndex"
              >
                <div
                  v-if="
                    attatchment.contents.filter((content) => {
                      return content.language == language.value;
                    }).length != 0
                  "
                >
                  <b>{{ language.name }}: </b>
                  <div
                    class="ellipsisOverflow fakeLink"
                    :title="
                      attatchment.contents.filter((content) => {
                        return content.language == language.value;
                      })[0].value
                    "
                    @click="
                      downloadFile(
                        attatchment.contents.filter((content) => {
                          return content.language == language.value;
                        })[0].fileId,
                        attatchment.contents.filter((content) => {
                          return content.language == language.value;
                        })[0].id,
                      )
                    "
                  >
                    {{
                      attatchment.contents.filter((content) => {
                        return content.language == language.value;
                      })[0].value
                    }}
                  </div>
                </div>
              </div>
            </td>
            <td
              v-else
              v-show="!isUpdating"
              v-b-tooltip.hover
              class="mb-2"
              placement="bottom"
              title="Anlage wird nur ersetzt wenn eine Datei ausgewählt ist. Sonst werden beim Speichern nur die Priorität und die Beschreibung überschrieben."
            >
              <b-alert
                v-show="missingUpdateFile"
                variant="danger"
                class="w-100 mt-4"
                show
                dismissible
              >
                Für jede neue Sprache muss eine Datei hochgeladen werden
              </b-alert>
              <div
                v-if="currentEditRowLanguages.indexOf('de-CH') != -1"
                class="mb-2"
              >
                <b class="inline-block">Deutsch</b>
                <input
                  ref="updatedFileGerman"
                  name="updatedFileGerman"
                  type="file"
                >
              </div>
              <div
                v-if="currentEditRowLanguages.indexOf('fr-CH') != -1"
                class="mb-2"
              >
                <b class="inline-block">Französisch</b>
                <input
                  ref="updatedFileFrench"
                  name="updatedFileFrench"
                  type="file"
                >
              </div>
              <div
                v-if="currentEditRowLanguages.indexOf('it-CH') != -1"
                class="mb-2"
              >
                <b class="inline-block">Italienisch</b>
                <input
                  ref="updatedFileItalian"
                  name="updatedFileItalian"
                  type="file"
                >
              </div>
            </td>
            <td v-if="currentEditRow != index">
              <div>
                <button
                  v-if="canUserEdit"
                  v-b-tooltip.hover
                  type="button"
                  placement="bottom"
                  title="Anlage bearbeiten"
                  class="btn btn-primary smallActionButton"
                  @click="
                    resetAttatchmentData();
                    currentEditRow = index;
                    setCurrentLanguages(attatchment.contents);
                  "
                >
                  <i class="fas fa-edit" />
                </button>

                <button
                  v-if="canUserEdit"
                  v-b-modal.deleteAttatchment
                  v-b-tooltip.hover
                  type="button"
                  placement="bottom"
                  title="Anlage löschen"
                  class="btn btn-danger smallActionButton ml-2"
                  @click="currentModalAttatchment = index"
                >
                  <i class="fas fa-trash-alt" />
                </button>
              </div>
            </td>
            <td
              v-else
              v-show="!isUpdating"
            >
              <button
                v-b-tooltip.hover
                type="button"
                placement="bottom"
                title="Speichern"
                class="btn btn-success smallActionButton ml-2"
                :disabled="requiredPropertiesEdit.length != 0"
                @click="saveAttatchment()"
              >
                <i class="fas fa-check" />
              </button>

              <button
                v-b-tooltip.hover
                type="button"
                placement="bottom"
                title="Abbrechen"
                class="btn btn-danger smallActionButton ml-2"
                @click="
                  resetAttatchmentData();
                  cancelEdit(index);
                "
              >
                <i class="fas fa-times" />
              </button>
            </td>
          </tr>
          <!-- Secound Row -->
          <tr :key="'ROW2_' + index">
            <td />

            <td
              v-if="currentEditRow != index"
              colspan="2"
            >
              <div>
                <b class="inline-block">Verwendung </b>
                <span
                  v-for="(
                    selectedItem, selectedItemIndex
                  ) in dropDownListUsedFor_selectedItems[index]"
                  :key="selectedItem.id"
                >
                  {{ selectedItem.description }}

                  <span
                    v-if="
                      selectedItemIndex + 1 !=
                        dropDownListUsedFor_selectedItems[index].length
                    "
                  >,
                  </span>
                </span>
              </div>
            </td>
            <td
              v-else
              v-show="!isUpdating"
              v-b-tooltip.hover
              colspan="2"
              class="mb-2"
              placement="bottom"
              title="Anlage wird nur ersetzt wenn eine Datei ausgewählt ist. Sonst werden beim Speichern nur die Priorität und die Beschreibung überschrieben."
            >
              <div>
                <b class="inline-block">Verwendung</b>
                <v-select
                  v-model="dropDownListUsedFor_selectedItems[currentEditRow]"
                  :items="dropDownListUsedFor[currentEditRow]"
                  item-text="description"
                  item-value="id"
                  return-object
                  multiple
                  @change="
                    updateDropDownList(
                      dropDownListUsedFor[currentEditRow],
                      dropDownListUsedFor_selectedItems[currentEditRow],
                    )
                  "
                />
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <b-modal
      id="deleteAttatchment"
      ref="deleteAttatchment"
      hide-footer
      title="Anlage löschen"
      no-fade
    >
      <div class="d-block mb-4">
        Sind Sie sicher, dass Sie diese Anlage löschen möchten?
      </div>
      <b-button
        variant="success"
        @click="deleteAttatchment()"
      >
        Ok
      </b-button>
      <b-button
        class="ml-2"
        variant
        @click="hideModal"
      >
        Abbrechen
      </b-button>
    </b-modal>

    <div
      v-if="!addAttatchment"
      class="w-100 mt-4"
      style="text-align: right"
    >
      <span>
        <button
          v-if="canUserEdit"
          v-b-tooltip.hover
          type="button"
          placement="bottom"
          title="Neue Datei hochladen"
          class="btn btn-success"
          @click="addAttatchment = true"
        >
          <i class="fas fa-plus" />
        </button>
      </span>
    </div>
    <div v-else>
      <h5 class="mt-4 mb-4">
        Datei hochladen
      </h5>

      <u-error-handling
        v-if="uploadErrorObject"
        :error-object="uploadErrorObject"
        :property-mapping="errorPropertyMappings"
      />
      <u-loader v-show="isUploading" />

      <form
        v-show="!isUploading"
        class="ml-2"
        enctype="multipart/form-data"
        @submit.prevent="uploadNewAttatchments"
      >
        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                for="filter"
                class="pl-0 col-2"
              >Priorität</label>
              <div class="col-9 pl-0">
                <v-flex sm6>
                  <v-text-field
                    v-model="newAttatchment.weight"
                    type="number"
                    label="Priorität"
                    class="mr-4 normalFontSize"
                  />
                </v-flex>
                <u-validation-checker
                  v-model="requiredPropertiesNew"
                  :is-empty="!newAttatchment.weight"
                  :section="validationSection"
                  property-name="weight"
                />
              </div>
            </b-row>
          </b-container>
        </div>
        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                for="filter"
                class="pl-0 col-2"
              >Sprachen</label>
              <div class="col-9 pl-0">
                <label>
                  <input
                    type="checkbox"
                    name="check"
                    :checked="newAttatchmentLanguages.indexOf('de-CH') != -1"
                    @change="languageCheckBoxChanged('de-CH', false)"
                  >
                  <span class="label-text">Deutsch</span>
                </label>
                <br>
                <label>
                  <input
                    type="checkbox"
                    name="check"
                    :checked="newAttatchmentLanguages.indexOf('fr-CH') != -1"
                    @change="languageCheckBoxChanged('fr-CH', false)"
                  >
                  <span class="label-text">Französisch</span>
                </label>
                <br>
                <label>
                  <input
                    type="checkbox"
                    name="check"
                    :checked="newAttatchmentLanguages.indexOf('it-CH') != -1"
                    @change="languageCheckBoxChanged('it-CH', false)"
                  >
                  <span class="label-text">Italienisch</span>
                </label>
                <u-validation-checker
                  v-model="requiredPropertiesNew"
                  :is-empty="newAttatchmentLanguages.length == 0"
                  :section="validationSection"
                  property-name="content.language"
                />
              </div>
            </b-row>
          </b-container>
        </div>
        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                for="filter"
                class="pl-0 col-2"
              >Beschreibung</label>
              <div class="col-9 pl-0">
                <div
                  v-for="(language, languageIndex) in allLanguages"
                  :key="languageIndex"
                >
                  <v-text-field
                    v-if="newAttatchmentLanguages.indexOf(language.value) != -1"
                    v-model="
                      newAttatchment.contents.filter((content) => {
                        return content.language == language.value;
                      })[0].value
                    "
                    type="text"
                    multi-line
                    rows="1"
                    auto-grow
                    :label="language.name"
                    class="mr-4 normalFontSize"
                  />
                </div>
                <u-validation-checker
                  v-model="requiredPropertiesNew"
                  :is-empty="
                    newAttatchment.contents.filter((content) => {
                      return content.value == '';
                    }).length != 0
                  "
                  :section="validationSection"
                  property-name="content.value"
                />
              </div>
            </b-row>
          </b-container>
        </div>

        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                for="filter"
                class="pl-0 col-2"
              >Verwendung</label>

              <div class="col-9 pl-0">
                <v-select
                  v-model="dropDownListDetails_selectedItems_new"
                  :items="dropDownListDetails_new"
                  item-text="description"
                  multiple
                  item-value="id"
                  return-object
                  @change="
                    updateDropDownList(
                      dropDownListDetails_new,
                      dropDownListDetails_selectedItems_new,
                    )
                  "
                />
              </div>
            </b-row>
          </b-container>
        </div>
        <div class="form-group">
          <b-container class="pb-0 pt-0">
            <b-row>
              <label
                for="filter"
                class="pl-0 col-2"
              >Dateien</label>
              <div class="col-9 pl-0">
                <div
                  v-if="newAttatchmentLanguages.indexOf('de-CH') != -1"
                  class="mb-2"
                >
                  <b-row>
                    <b-col cols="2">
                      <b>Deutsch</b>
                    </b-col>
                    <b-col cols="10">
                      <input
                        ref="uploadFileGerman"
                        name="uploadFileGerman"
                        type="file"
                      >
                    </b-col>
                  </b-row>
                </div>
                <div
                  v-if="newAttatchmentLanguages.indexOf('fr-CH') != -1"
                  class="mb-2"
                >
                  <b-row>
                    <b-col cols="2">
                      <b>Französisch</b>
                    </b-col>
                    <b-col cols="10">
                      <input
                        ref="uploadFileFrench"
                        name="uploadFileFrench"
                        type="file"
                      >
                    </b-col>
                  </b-row>
                </div>
                <div
                  v-if="newAttatchmentLanguages.indexOf('it-CH') != -1"
                  class="mb-2"
                >
                  <b-row>
                    <b-col cols="2">
                      <b>Italienisch</b>
                    </b-col>
                    <b-col cols="10">
                      <input
                        ref="uploadFileItalian"
                        name="uploadFileItalian"
                        type="file"
                      >
                    </b-col>
                  </b-row>
                </div>
                <b-alert
                  v-show="noLanguage"
                  variant="danger"
                  class="w-100 mt-4"
                  show
                  dismissible
                >
                  Wählen Sie mindestens eine Sprache aus.
                </b-alert>
                <b-alert
                  v-show="missingFile"
                  variant="danger"
                  class="w-100 mt-4"
                  show
                  dismissible
                >
                  Für jede Sprache muss eine Datei hochgeladen werden
                </b-alert>
              </div>
            </b-row>
          </b-container>
        </div>
        <b-btn
          variant="success"
          type="submit"
          :disabled="requiredPropertiesNew.length != 0"
        >
          Hinzufügen
        </b-btn>
        <b-btn
          variant="danger"
          class="ml-2"
          @click="cancelAddAttatchment"
        >
          Abbrechen
        </b-btn>
      </form>
    </div>
  </div>
</template>

<script>
import {
  GetAttatchments,
  DownloadFile,
  UploadAttatchmentFile,
  UploadAttatchment,
  UpdateAttatchment,
  UpdateAttatchmentFile,
  DeleteAttatchment,
  GetDropdownList,
} from "@/services/contract.edition.attatchments.service.js";

export default {
  props: {
    contractId: {
      type: String,
      default: "",
    },
    versionId: {
      type: Number,
      default: 0,
    },
    editionId: {
      type: Number,
      default: 0,
    },
    canUserEdit: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      validationSection: "Anlagen",
      allAttatchments: [],
      allOriginalAttatchments: [],
      isLoading: false,
      isUploading: false,
      isUpdating: false,
      errorObject: null,
      uploadErrorObject: null,
      addAttatchment: false,
      currentModalAttatchment: -1,
      currentEditRow: -1,
      currentEditRowLanguages: [],
      errorPropertyMappings: [],
      missingFile: false,
      missingUpdateFile: false,
      noLanguage: false,
      numberOfFilesToUpload: 0,
      uploadedFiles: [],

      newAttatchment: {
        id: 0,
        documentClass: 1,
        contents: [
          {
            id: 0,
            value: "",
            language: "de-CH",
            fileId: 0,
            documentId: 0,
          },
        ],
        dropDownListUsedFor: {},
        weight: "0",
        archived: false,
        editionId: 0,
        code: "newAttatchment",
        contentSortingOrder: 0,
        order: 0,
      },
      allLanguages: [
        { name: "Deutsch", value: "de-CH" },
        { name: "Französisch", value: "fr-CH" },
        { name: "Italienisch", value: "it-CH" },
      ],
      newAttatchmentLanguages: ["de-CH"],
      requiredPropertiesEdit: [],
      requiredPropertiesNew: [],
      dropDownListDetails_selectedItems_new: [],
      dropDownListDetails_new: [],
      dropDownListUsedFor: [],
      dropDownListUsedFor_selectedItems: [],
    };
  },
  created() {
    this.loadAttatchments();
  },
  methods: {
    loadDropdowns() {
      GetDropdownList(
        (response) => {
          this.dropDownListDetails_new = response.dropDownListDetails;
          this.dropDownListDetails_selectedItems_new = this.setDropDownList(
            response.dropDownListDetails,
          );
          this.newAttatchment.dropDownListUsedFor = response;
        },
        (error) => {
          false;
          this.loadingError = true;
          this.errorObject = error;
        },
      );
    },
    updateDropDownList(dropDownListDetails, dropDownListDetails_selectedItems) {
      if (dropDownListDetails_selectedItems) {
        dropDownListDetails.forEach((dropDownListDetail, index) => {
          const findItem = dropDownListDetails_selectedItems.find(
            (dropDownListUsedFor_selectedItem) =>
              dropDownListUsedFor_selectedItem.dropDownListId ==
                dropDownListDetail.dropDownListId &&
              dropDownListUsedFor_selectedItem.description ==
                dropDownListDetail.description,
          );
          if (findItem) {
            dropDownListDetails[index].selected = true;
          } else {
            dropDownListDetails[index].selected = false;
          }
        });
      }
    },

    setDropDownListUsedFor(allDataEnteries) {
      allDataEnteries.forEach((entry) => {
        const selectedListItems = [];
        this.dropDownListUsedFor.push(
          entry.dropDownListUsedFor.dropDownListDetails,
        );

        entry.dropDownListUsedFor.dropDownListDetails.forEach(
          (dropDownListDetail) => {
            if (dropDownListDetail.selected == true) {
              selectedListItems.push(dropDownListDetail);
            }
          },
        );
        this.dropDownListUsedFor_selectedItems.push(selectedListItems);
      });
    },
    setDropDownList(dropDownListDetails) {
      let dropDownListDetails_selectedItems = [];
      let arrayPos = 0;
      dropDownListDetails.forEach((dropDownListDetail) => {
        if (dropDownListDetail.selected) {
          dropDownListDetails_selectedItems.push(dropDownListDetail);
        }
        arrayPos++;
      });
      if (arrayPos == dropDownListDetails.length) {
        return dropDownListDetails_selectedItems;
      }
    },

    createErrorPropertyMappings() {
      var mapping = {};
      var index = 0;

      var languageArray = this.addAttatchment
        ? this.newAttatchmentLanguages
        : this.currentEditRowLanguages;

      languageArray.map((availableLanguage) => {
        var language = this.allLanguages.filter((language) => {
          return language.value == availableLanguage;
        });

        mapping[`Contents[${index}].Value`] =
          `${language[0].name} Beschreibung`;

        index++;
      });

      return mapping;
    },
    setCurrentLanguages(contents) {
      this.currentEditRowLanguages = [];
      contents.map((content) => {
        this.currentEditRowLanguages.push(content.language);
      });
    },
    languageCheckBoxChanged(language, isEditLanguage) {
      var languages;
      var attatchment;

      if (isEditLanguage) {
        languages = this.currentEditRowLanguages;
        attatchment = this.allAttatchments[this.currentEditRow];
      } else {
        languages = this.newAttatchmentLanguages;
        attatchment = this.newAttatchment;
      }

      if (languages.indexOf(language) == -1) {
        languages.push(language);

        // add a new langauge Content to

        attatchment.contents.push({
          id: 0,
          value: "",
          language: language,
          fileId: "",
          documentId: 0,
        });
      } else {
        languages.splice(languages.indexOf(language), 1);
        var languageContentToRemove = attatchment.contents.filter((content) => {
          return content.language == language;
        });
        if (languageContentToRemove) {
          attatchment.contents.splice(
            attatchment.contents.indexOf(
              attatchment.contents.filter((content) => {
                return content.language == language;
              })[0],
            ),
            1,
          );
        }
      }
    },
    loadAttatchments() {
      this.loadDropdowns();
      this.dropDownListUsedFor_selectedItems = [];
      this.isLoading = true;
      this.errorObject = null;

      GetAttatchments(
        this.contractId,
        this.versionId,
        this.editionId,
        (response) => {
          this.isLoading = false;

          this.setDropDownListUsedFor(response);
          this.allAttatchments = response;
          this.allOriginalAttatchments = JSON.parse(JSON.stringify(response));
        },
        (error) => {
          false;
          this.loadingError = true;
          this.errorObject = error;
        },
      );
    },
    hasMissingFile() {
      var missingFiles = false;

      this.newAttatchment.contents.map((newContent) => {
        switch (newContent.language) {
          case "de-CH":
            var germanUpload = this.$refs["uploadFileGerman"];
            if (germanUpload.files.length == 0 || !germanUpload.files[0]) {
              missingFiles = true;
            }
            break;

          case "fr-CH":
            var frenchUpload = this.$refs["uploadFileFrench"];
            if (frenchUpload.files.length == 0 || !frenchUpload.files[0]) {
              missingFiles = true;
            }
            break;

          case "it-CH":
            var italianUpload = this.$refs["uploadFileItalian"];
            if (italianUpload.files.length == 0 || !italianUpload.files[0]) {
              missingFiles = true;
            }
            break;

          default:
            break;
        }
      });

      return missingFiles;
    },
    waitForAllUploadsAndContinue() {
      // only post the new attatchment if all uploads were successful.
      if (this.uploadedFiles.length == this.newAttatchment.contents.length) {
        UploadAttatchment(
          this.contractId,
          this.versionId,
          this.editionId,
          this.newAttatchment,
          () => {
            this.loadAttatchments();
            this.isUploading = false;
            this.cancelAddAttatchment();
          },
          (error) => {
            this.uploadErrorObject = error;
            this.isUploading = false;
          },
        );
      }
    },
    waitForAllUpdatesAndContinue() {
      // only update the attatchment if all uploads/updates were successful.
      if (this.uploadedFiles.length == this.numberOfFilesToUpload) {
        UpdateAttatchment(
          this.contractId,
          this.versionId,
          this.editionId,
          this.allAttatchments[this.currentEditRow].id,
          this.allAttatchments[this.currentEditRow],
          () => {
            this.loadAttatchments();
            this.cancelEdit();
            this.isUpdating = false;
          },
          (error) => {
            this.errorObject = error;
            this.isUpdating = false;
          },
        );
      }
    },
    uploadNewAttatchments() {
      this.uploadErrorObject = null;
      this.uploadedFiles = [];
      this.missingFile = false;
      this.noLanguage = false;
      this.isUploading = true;
      this.errorPropertyMappings = this.createErrorPropertyMappings();

      if (this.newAttatchment.contents.length == 0) {
        this.noLanguage = true;
        this.isUploading = false;
        return;
      }

      if (this.hasMissingFile()) {
        this.missingFile = true;
        this.isUploading = false;
        return;
      }
      this.newAttatchment.dropDownListUsedFor.dropDownListDetails =
        this.dropDownListDetails_new;

      this.newAttatchment.contents.map((newContent) => {
        switch (newContent.language) {
          case "de-CH":
            var germanUpload = this.$refs["uploadFileGerman"];

            var formDataDe = new FormData();
            formDataDe.append("file", germanUpload.files[0]);

            UploadAttatchmentFile(
              formDataDe,
              (createdFileId) => {
                this.uploadedFiles.push(createdFileId);
                newContent.fileId = createdFileId;
                this.waitForAllUploadsAndContinue();
              },
              (error) => {
                this.uploadErrorObject = error;
                this.isUploading = false;
              },
            );
            break;

          case "fr-CH":
            var frenchUpload = this.$refs["uploadFileFrench"];
            var formDataFr = new FormData();
            formDataFr.append("file", frenchUpload.files[0]);

            UploadAttatchmentFile(
              formDataFr,
              (createdFileId) => {
                this.uploadedFiles.push(createdFileId);
                newContent.fileId = createdFileId;
                this.waitForAllUploadsAndContinue();
              },
              (error) => {
                this.uploadErrorObject = error;
                this.isUploading = false;
              },
            );
            break;

          case "it-CH":
            var italianUpload = this.$refs["uploadFileItalian"];
            var formDataIt = new FormData();
            formDataIt.append("file", italianUpload.files[0]);

            UploadAttatchmentFile(
              formDataIt,
              (createdFileId) => {
                this.uploadedFiles.push(createdFileId);
                newContent.fileId = createdFileId;
                this.waitForAllUploadsAndContinue();
              },
              (error) => {
                this.uploadErrorObject = error;
                this.isUploading = false;
              },
            );
            break;

          default:
            break;
        }
      });
    },
    cancelEdit() {
      this.requiredPropertiesEdit = [];
      this.currentEditRow = -1;
      this.errorObject = null;
    },
    cancelAddAttatchment() {
      this.requiredPropertiesNew = [];
      this.addAttatchment = false;
      this.newAttatchmentLanguages = ["de-CH"];
      this.newAttatchment = {
        id: 0,
        documentClass: 1,
        contents: [
          {
            id: 0,
            value: "",
            language: "de-CH",
            fileId: 0,
            documentId: 0,
          },
        ],
        weight: "0",
        archived: false,
        editionId: 0,
        code: "newAttatchment",
        contentSortingOrder: 0,
        order: 0,
      };
    },
    hideModal() {
      this.$refs["deleteAttatchment"].hide();
    },
    getAllUpdateFiles() {
      var allFiles = [];

      var germanUpdate = this.$refs["updatedFileGerman"];
      if (germanUpdate && germanUpdate[0] && germanUpdate[0].files[0]) {
        allFiles.push({ language: "de-CH", file: germanUpdate[0].files[0] });
      }
      var frenchUpdate = this.$refs["updatedFileFrench"];
      if (frenchUpdate && frenchUpdate[0] && frenchUpdate[0].files[0]) {
        allFiles.push({ language: "fr-CH", file: frenchUpdate[0].files[0] });
      }
      var italianUpdate = this.$refs["updatedFileItalian"];
      if (italianUpdate && italianUpdate[0] && italianUpdate[0].files[0]) {
        allFiles.push({ language: "it-CH", file: italianUpdate[0].files[0] });
      }

      return allFiles;
    },
    updateAttatchments() {
      // this.allAttatchments[0].dropDownListUsedFor = this.dropDownListUsedFor;
      // first, reset the array with uploaded files
      this.uploadedFiles = [];

      var allContentsWithNewFiles = this.getAllUpdateFiles();

      this.numberOfFilesToUpload = allContentsWithNewFiles.length;

      this.missingUpdateFile = false;
      this.allAttatchments[
        this.currentEditRow
      ].dropDownListUsedFor.dropDownListDetails =
        this.dropDownListUsedFor[this.currentEditRow];

      // First, reset all fileId of new languages
      this.allAttatchments[this.currentEditRow].contents.map((content) => {
        if (content.documentId == 0) {
          content.fileId = "";
        }
      });

      // Check if all new languages have a file selected
      this.allAttatchments[this.currentEditRow].contents.map((content) => {
        if (content.fileId == "") {
          if (
            allContentsWithNewFiles.filter((newFile) => {
              return newFile.language == content.language;
            }).length == 0
          ) {
            this.missingUpdateFile = true;
          }
        }
      });

      // if not all new languages have a file, we stop the upload.
      if (this.missingUpdateFile) {
        this.isUpdating = false;
        return;
      }

      // if there are no new files, we just update the contents
      if (this.numberOfFilesToUpload == 0) {
        this.waitForAllUpdatesAndContinue();
        return;
      }

      // Upload all new Files and update all existing files
      this.allAttatchments[this.currentEditRow].contents.map((content) => {
        if (content.fileId == "") {
          // if the content is new and didnt have a file yet

          var newFileToUpload = allContentsWithNewFiles.filter((newFile) => {
            return newFile.language == content.language;
          })[0];

          var formData = new FormData();
          formData.append("file", newFileToUpload.file);

          UploadAttatchmentFile(
            formData,
            (createdFileId) => {
              content.fileId = createdFileId;
              this.uploadedFiles.push(createdFileId);
              this.waitForAllUpdatesAndContinue();
            },
            (error) => {
              this.errorObject = error;
              this.isUpdating = false;
            },
          );
        } else {
          // first, check if the language even has a new file to upload
          var filesToUpload = allContentsWithNewFiles.filter((newFile) => {
            return newFile.language == content.language;
          });

          if (filesToUpload.length == 1) {
            var existingFileFormData = new FormData();
            existingFileFormData.append("file", filesToUpload[0].file);

            // if the content already had a file
            UpdateAttatchmentFile(
              this.contractId,
              this.versionId,
              this.editionId,
              content.documentId,
              content.id,
              content.fileId,
              existingFileFormData,
              () => {
                this.uploadedFiles.push(content.fileId);
                this.waitForAllUpdatesAndContinue();
              },
              (error) => {
                this.errorObject = error;
                this.isUpdating = false;
              },
            );
          }
        }
      });
    },
    saveAttatchment() {
      // This is quite complicated, as the files need to be uploaded first, then the properties have to be updated
      // First, we check if all the new languages have a file. If not, stop.
      // Then we Upload all the new files (UploadAttatchmentFile) and update (UpdateAttatchmentFile) the existing files
      // When all the files are uploaded, we update the rest of the document (all properties and contents)
      // Fortunatly, the backend handles uploaded files that are not used. If a file uploads but the property update fails,
      // we dont have to do anything about it (except showing a message why the property upload failed)
      // Also, I didn't have enought time to refactor the code, my guess is that it could be done way easier.
      this.isUpdating = true;
      this.errorObject = null;
      this.errorPropertyMappings = this.createErrorPropertyMappings();
      this.updateAttatchments();
    },
    deleteAttatchment() {
      this.errorObject = null;

      var attatchmentToDelete =
        this.allAttatchments[this.currentModalAttatchment];

      DeleteAttatchment(
        this.contractId,
        this.versionId,
        this.editionId,
        attatchmentToDelete.id,
        () => {
          this.hideModal();
          this.loadAttatchments();
        },
        (error) => {
          this.hideModal();
          this.errorObject = error;
        },
      );
    },
    resetAttatchmentData() {
      this.requiredPropertiesEdit = [];
      this.allAttatchments = JSON.parse(
        JSON.stringify(this.allOriginalAttatchments),
      );
    },
    downloadFile(fileId, contentId) {
      DownloadFile(
        this.contractId,
        this.versionId,
        this.editionId,
        fileId,
        contentId,
        (response, fileName) => {
          // Workaround for IE
          if (typeof window.navigator.msSaveBlob !== "undefined") {
            window.navigator.msSaveBlob(new Blob([response]), fileName);
          } else {
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
          }
        },
        (error) => {
          console.log(error);
        },
      );
    },
  },
};
</script>

<style scoped>
.smallActionButton {
  width: 25px;
  height: 25px;
  font-size: 17px;
  padding: 0px;
}

.normalFontSize {
  font-size: 1rem;
}
.ellipsisOverflow {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  margin-right: 10px;
}

.attatchmentsTable {
  font-size: small;
  table-layout: fixed;
}

.attatchmentsTable tbody tr {
  border-bottom: 4px solid #ddd;
}
.attatchmentsTable th {
  padding-right: 30px;
}
.attatchmentsTable thead th:nth-child(1) {
  width: 8%;
}
.attatchmentsTable thead th:nth-child(2) {
  width: 10%;
}
.attatchmentsTable thead th:nth-child(3) {
  width: 40%;
}
.attatchmentsTable thead th:nth-child(4) {
  width: 30%;
}
.attatchmentsTable tr td:first-child {
  border-bottom: 2px solid white;
}
.attatchmentsTable tr td {
  padding-bottom: 10px;
  padding-top: 10px;
  vertical-align: top;
}
.attatchmentsTable tr:nth-child(2n) td {
  padding-top: 10px;
}

.attatchmentsTable .fakeLink {
  color: red;
  cursor: pointer;
  width: fit-content;
  max-width: 100%;
}
.attatchmentsTable .fakeLink:hover {
  text-decoration: underline;
}
.inline-block {
  display: block;
}
</style>
